import { Controller } from "@hotwired/stimulus"

// QAN-691: Credit Repair Dashboard interactions.
//
// Both views are rendered server-side; this toggles between them (remembered in
// localStorage), saves the inline editors without a full reload, and drives the
// Start Round / Add Note modals.
//
// Mounted on the Case File too, which has no view toggle and no table — every
// target here is optional and guarded, so one controller serves both pages rather
// than two controllers duplicating `send()` and the modal shell.
export default class extends Controller {
  static targets = ["tableView", "cardView", "tableButton", "cardButton", "modal", "modalBody"]

  static STORAGE_KEY = "creditRepairDashboardView"

  connect() {
    if (!this.hasTableViewTarget && !this.hasCardViewTarget) return

    const saved = window.localStorage.getItem(this.constructor.STORAGE_KEY)
    if (saved === "card") {
      this.showCard()
    } else {
      this.showTable()
    }
  }

  // ── View toggle ──

  showTable() {
    if (!this.hasTableViewTarget) return
    this.tableViewTarget.classList.remove("hidden")
    if (this.hasCardViewTarget) this.cardViewTarget.classList.add("hidden")
    this.setActive(this.tableButtonTarget, this.cardButtonTarget)
    window.localStorage.setItem(this.constructor.STORAGE_KEY, "table")
  }

  showCard() {
    if (!this.hasCardViewTarget) return
    this.cardViewTarget.classList.remove("hidden")
    if (this.hasTableViewTarget) this.tableViewTarget.classList.add("hidden")
    this.setActive(this.cardButtonTarget, this.tableButtonTarget)
    window.localStorage.setItem(this.constructor.STORAGE_KEY, "card")
  }

  // Moves the design system's `active` class rather than swapping colour
  // utilities, so the toggle keeps the square navy treatment every other admin
  // list uses (admin_design.scss owns `.view-toggle-btn.active`).
  setActive(on, off) {
    if (on) on.classList.add("active")
    if (off) off.classList.remove("active")
  }

  // ── Stage, inline ──
  //
  // The grid shows Stage as text and swaps to a select + Save on click. That is
  // the mockup's shape, and it is also what keeps a long list readable: ten open
  // dropdowns down a column read as a form, not as a report.

  editStage(event) {
    const row = event.currentTarget.closest("[data-credit-repair-row]")
    if (!row) return

    const display = row.querySelector("[data-stage-display]")
    const editor = row.querySelector("[data-stage-editor]")
    if (!display || !editor) return

    display.classList.add("hidden")
    editor.classList.remove("hidden")
    editor.classList.add("flex")
  }

  async saveStage(event) {
    const button = event.currentTarget
    const row = button.closest("[data-credit-repair-row]")
    const select = row ? row.querySelector("[data-stage-select]") : null
    if (!button.dataset.url || !select) return

    await this.persist(button, button.dataset.url, "PATCH", { stage: select.value })
  }

  // The Case File's header dropdown, which has no separate Save — it is the one
  // place Stage is both the display and the control.
  async saveStageDirect(event) {
    const select = event.currentTarget
    await this.persist(select, select.dataset.url, "PATCH", { stage: select.value })
  }

  // ── Bureau status ──
  //
  // Saves on change and independently of the other three. A reload follows because
  // one bureau going Complete moves the Stage, the Status, the progress bar and
  // the follow-up date — repainting only the cell that was touched would leave the
  // page quietly wrong about the other four.
  async saveBureau(event) {
    const select = event.currentTarget
    await this.persist(select, select.dataset.url, "PATCH", {
      bureau: select.dataset.bureau,
      status: select.value
    })
  }

  async saveTier(event) {
    const select = event.currentTarget
    await this.persist(select, select.dataset.url, "PATCH", { tier: select.value })
  }

  async toggleHot(event) {
    const button = event.currentTarget
    await this.persist(button, button.dataset.url, "PATCH", {})
  }

  async toggleOnHold(event) {
    const button = event.currentTarget
    await this.persist(button, button.dataset.url, "PATCH", {})
  }

  async completeOnboardingCall(event) {
    const button = event.currentTarget
    await this.persist(button, button.dataset.url, "POST", {})
  }

  async recordConsent(event) {
    const button = event.currentTarget
    if (!window.confirm("Record that this client consented to start Credit Repair before their call?")) return

    await this.persist(button, button.dataset.url, "POST", {})
  }

  async completeRound(event) {
    const button = event.currentTarget
    await this.persist(button, button.dataset.url, "POST", {})
  }

  // Fired by either field's change, not by a Save button — the mockup has none, and
  // the note under the cards promises that a bordered field saves on change.
  async saveCfpbLogin(event) {
    const field = event.currentTarget
    const card = field.closest(".rounded-lg")
    const username = card ? card.querySelector("[data-cfpb-username]") : null
    const password = card ? card.querySelector("[data-cfpb-password]") : null
    if (!username) return

    // An empty password field means "leave the stored one alone", so it is omitted
    // rather than sent blank — otherwise correcting the username would wipe the
    // password.
    const payload = { username: username.value }
    if (password && password.value) payload.password = password.value

    await this.persist(field, field.dataset.url, "PATCH", payload, { reload: false })
  }

  // Reveal / hide, as drawn. Swaps the input type and the two icons; nothing is
  // fetched, because the value is already on the page.
  toggleCfpbPassword(event) {
    event.preventDefault()
    const button = event.currentTarget
    const wrapper = button.parentElement
    const input = wrapper ? wrapper.querySelector("[data-cfpb-password]") : null
    if (!input) return

    const revealed = input.type === "text"
    input.type = revealed ? "password" : "text"

    const show = button.querySelector("[data-eye-show]")
    const hide = button.querySelector("[data-eye-hide]")
    if (show) show.classList.toggle("hidden", !revealed)
    if (hide) hide.classList.toggle("hidden", revealed)
  }

  // The Contact card's four fields. They write to the DEAL, so a failure here is a
  // validation error worth showing rather than swallowing — persist() already
  // surfaces one.
  async saveContact(event) {
    const input = event.currentTarget
    const field = input.dataset.contactField
    if (!field) return

    await this.persist(input, input.dataset.url, "PATCH", { [field]: input.value }, { reload: false })
  }

  // ── Modals ──

  openModal(event) {
    event.preventDefault()
    this.showModalTemplate(event.currentTarget.dataset.modalTemplate)
  }

  showModalTemplate(templateId) {
    if (!templateId || !this.hasModalTarget || !this.hasModalBodyTarget) return

    const template = document.getElementById(templateId)
    if (!template) return

    this.modalBodyTarget.innerHTML = ""
    this.modalBodyTarget.appendChild(template.content.cloneNode(true))
    this.modalTarget.classList.remove("hidden")
  }

  closeModal(event) {
    if (event) event.preventDefault()
    if (!this.hasModalTarget) return

    this.modalTarget.classList.add("hidden")
    if (this.hasModalBodyTarget) this.modalBodyTarget.innerHTML = ""
  }

  async startRound(event) {
    const button = event.currentTarget
    const body = this.modalBodyTarget
    const bureaus = Array.from(body.querySelectorAll("[data-round-bureau]:checked")).map((el) => el.value)

    if (bureaus.length === 0) {
      this.setModalError("Select at least one bureau for this round.")
      return
    }

    const payload = {
      bureaus: bureaus,
      date_processed: this.modalValue("[data-round-date]"),
      note: this.modalValue("[data-round-note]")
    }

    const result = await this.send(button.dataset.url, "POST", payload)
    if (result) {
      window.location.reload()
    } else {
      this.setModalError("Could not start the round. Please try again.")
    }
  }

  async addNote(event) {
    const button = event.currentTarget
    const body = this.modalBodyTarget
    const checked = body.querySelector("[data-note-internal]:checked")

    const payload = {
      title: this.modalValue("[data-note-title]"),
      body: this.modalValue("[data-note-body]"),
      internal: checked ? checked.value : "false"
    }

    if (!payload.body) {
      this.setModalError("A note needs some text.")
      return
    }

    const result = await this.send(button.dataset.url, "POST", payload)
    if (result) {
      window.location.reload()
    } else {
      this.setModalError("Could not save the note. Please try again.")
    }
  }

  modalValue(selector) {
    const el = this.hasModalBodyTarget ? this.modalBodyTarget.querySelector(selector) : null
    return el ? el.value : ""
  }

  setModalError(message) {
    const el = this.hasModalBodyTarget ? this.modalBodyTarget.querySelector("[data-modal-error]") : null
    if (!el) return

    el.textContent = message
    el.classList.remove("hidden")
  }

  // ── Shared write path ──
  //
  // Every inline editor goes through here. A successful write reloads: the
  // automation behind this page moves several fields at once from a single edit
  // (a bureau completing advances the Stage, which re-derives the Status and may
  // re-date Expected Completion), and a partial repaint would show the one field
  // that was edited next to four that are now stale.
  // `reload` defaults to true because most writes here change something ELSE on the
  // page: a stage change re-derives Status, Date Started and Expected Completion, and
  // a bureau change can advance the stage on its own. Those have to be re-read.
  //
  // A plain text field changes only itself, and its value is already on screen —
  // reloading there costs the scroll position and the caret for nothing.
  async persist(el, url, method, payload, { reload = true } = {}) {
    if (!url) return

    const feedback = this.feedbackFor(el)
    this.setFeedback(feedback, "Saving…", "text-gray-400")

    const result = await this.send(url, method, payload)
    if (!result) {
      this.setFeedback(feedback, "Could not save", "text-red-600")
      return
    }

    if (reload) {
      window.location.reload()
    } else {
      this.setFeedback(feedback, "Saved", "text-gray-400")
    }
  }

  async send(url, method, body) {
    const csrfMeta = document.querySelector('meta[name="csrf-token"]')
    const csrfToken = csrfMeta ? csrfMeta.content : ""

    try {
      const response = await fetch(url, {
        method: method,
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify(body)
      })
      if (!response.ok) return null
      return await response.json()
    } catch {
      return null
    }
  }

  feedbackFor(el) {
    const row = el.closest("[data-credit-repair-row]")
    if (row) {
      const scoped = row.querySelector("[data-field-feedback]")
      if (scoped) return scoped
    }

    return this.element.querySelector("[data-field-feedback]")
  }

  setFeedback(el, text, className) {
    if (!el) return

    el.textContent = text
    el.className = `text-xs ${className}`
  }
}
